import { observable, action, makeObservable } from 'mobx';
import moment from 'moment/min/moment-with-locales';
import agent from '../agent';
import commonStore from './commonStore';

import { isISOWeekend } from '../../utils/commonMethods';

const getDefaultHoursForDay = (isHoliday) => {
    return isHoliday ? 1440 : 900;
}

export const getDefaultMinTimesheetHours = (isFieldOperative, isHolidayOrWeekend, isSaturdayWithoutHoliday) => {
    if (!isFieldOperative)
        return 0.5 * 60;
    else if (!isHolidayOrWeekend || isSaturdayWithoutHoliday)
        return 2 * 60;
    else
        return 2.5 * 60;
}

const getDefaultTimesheetConfigs = (isHolidayOrWeekend, overtimeTypes, isFieldOperative, isSaturdayWithoutHoliday) => {
    const defaultTimesheetConfigs = [];
    const [ot1, ot2] = Object.values(overtimeTypes);
    const minTimesheetHours = getDefaultMinTimesheetHours(isFieldOperative, isHolidayOrWeekend, isSaturdayWithoutHoliday);
    if (ot1 && ot2) {
        defaultTimesheetConfigs.push({
            overtime_code: isHolidayOrWeekend && ot2 ? ot2.code : ot1.code,
            from: 6 * 60,
            to: 20 * 60,
            min_hours_in_mins: minTimesheetHours
        });
        defaultTimesheetConfigs.push({
            overtime_code: isHolidayOrWeekend || ot2 ? ot2.code : ot1.code,
            from: 20 * 60,
            to: 6 * 60,
            min_hours_in_mins: minTimesheetHours
        });
        return defaultTimesheetConfigs;
    }
}

const generateWeekData = (startOfWeek, overtimeTypes, isFieldOperative) => {
    const urgentWorkSettings = commonStore.config && commonStore.config.client && commonStore.config.client.data
        && commonStore.config.client.data.basicRules && commonStore.config.client.data.basicRules.urgentWorkSettings;
    const adminFieldWorkConfigs = urgentWorkSettings && urgentWorkSettings.defaultFieldWorkTimesheetConfigs;
    const adminHolidayFieldWorkConfigs = urgentWorkSettings && urgentWorkSettings.defaultHolidayFieldWorkTimesheetConfigs;
    const adminCustomerCareConfigs = urgentWorkSettings && urgentWorkSettings.defaultCustomerCareTimesheetConfigs;
    const adminHolidayCustomerCareConfigs = urgentWorkSettings && urgentWorkSettings.defaultHolidayCustomerCareTimesheetConfigs;

    const weekDays = {};
    let curentDay = startOfWeek.clone();
    for (let i = 0; i < 7; i++) {
        const dayKey = curentDay.format('YYYY-MM-DD');
        const isHoliday = false;
        const isHolidayOrWeekend = isHoliday || isISOWeekend(curentDay.format('YYYY-MM-DD')) || commonStore.isHoliday(curentDay.format('YYYY-MM-DD'));
        const isSaturdayWithoutHoliday = moment(curentDay).day() === 6 && !isHoliday;
        let adminConfigs;
        if (isFieldOperative) {
            adminConfigs = isHolidayOrWeekend ? adminHolidayFieldWorkConfigs : adminFieldWorkConfigs;
        } else {
            adminConfigs = isHolidayOrWeekend ? adminHolidayCustomerCareConfigs : adminCustomerCareConfigs;
        }
        const timesheetConfigs = adminConfigs && adminConfigs.length > 0
            ? adminConfigs.map(config => Object.assign({}, config))
            : getDefaultTimesheetConfigs(isHolidayOrWeekend, overtimeTypes, isFieldOperative, isSaturdayWithoutHoliday);
        weekDays[dayKey] = {
            key: dayKey,
            date: curentDay.format('YYYY-MM-DD'),
            dayName: curentDay.format('ddd'),
            isWeekend: isHolidayOrWeekend,
            isChecked: false,
            hours: getDefaultHoursForDay(isHolidayOrWeekend),
            timesheetConfigs
        };
        curentDay.add(1, 'days');
    }
    return weekDays;
}

function getDayNames(for_date) {
    return for_date.map(date => {
        return {
            isHoliday: isISOWeekend(date) || commonStore.isHoliday(date),
            day: moment(date).format('ddd')
        };
    });
}

const transformUrgentCallRecords = (urgentCallsData, urgentWorkSettings) => {
    if (!urgentCallsData || !Array.isArray(urgentCallsData)) {
        return [];
    }
    const { allowStandbyCalls = false, allowStandbySyncForFixedSalary = false, allowStandbySyncForHourlySalary = false } = urgentWorkSettings || {};
    return urgentCallsData.map(uc => {
        const dayNames = getDayNames(uc.for_dates);
        const standbyDayNames = getDayNames(uc.standby_for_dates);
        const paymentMode = uc.User?.payment_mode;
        const ignoreStandbySync = (paymentMode === 'hourly' && !allowStandbySyncForHourlySalary)
            || (paymentMode === 'fixed' && !allowStandbySyncForFixedSalary);
        let unsyncedStandbyAllowance = ignoreStandbySync ? (uc.standby_for_dates?.length || 0) : 0;

        return {
            user_id: uc.user_id,
            employee: `${uc.User.first_name} ${uc.User.last_name}`,
            is_field_operative: uc.is_field_operative,
            type: uc.is_field_operative ? 'Field Operative' : 'Customer Care',
            days: [...new Set(dayNames)],
            standby_days: [...new Set(standbyDayNames)],
            isSynced: uc.int_details?.length && uc.int_details.every(d => {
                if (d && d.synced) return true;
                if (allowStandbyCalls && unsyncedStandbyAllowance > 0) {
                    unsyncedStandbyAllowance--;
                    return true;
                }
                return false;
            }),
            isAwaiting: uc.int_details?.length && uc.int_details.some(d => d && d.awaiting)

        };
    });
}

class UrgentCallsStore {
    constructor() {
        makeObservable(this, {
            loading: observable,
            getDefaultUrgentCallWeekData: action,
            setLoading: action,
            loadUrgentCallsForWeek: action,
            loadEmployeeUrgentCalls: action,
            saveUrgentCalls: action,
            updateUrgentCalls: action,
            deleteUrgentCall: action,
            syncToPayroll: action,
            desyncToPayroll: action,
            deleteUrgentCallWeek: action,
            loadEmployeeUrgentCallForDate: action,
            updateCustomerCareHours: action
        });
    }

    loading = false;

    getDefaultUrgentCallWeekData = (weekNumber, overtimeTypes, isFieldOperative, year) => {
        const startOfWeek = moment().isoWeekYear(year).isoWeek(weekNumber).startOf('isoWeek');
        return generateWeekData(startOfWeek, overtimeTypes, isFieldOperative);
    }

    setLoading = (loading) => {
        this.loading = loading;
    }

    loadUrgentCallWeeklyCounts = (year) => {
        return agent.UrgentCalls.getWeekCounts({ year })
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error loading urgent call counts:', error);
                    throw error;
                })
            )
    }

    loadUrgentCallsForWeek = (weekNumber, params = {}, urgentWorkSettings) => {
        return agent.UrgentCalls.getUrgentForTheWeek(weekNumber, params)
            .then(
                action((response) => {
                    return transformUrgentCallRecords(response, urgentWorkSettings);
                })
            )
            .catch(
                action((error) => {
                    console.error('Error loading urgent calls for week:', error);
                    throw error;
                })
            )
    }

    getUrgentForTheDay = () => {
        return agent.UrgentCalls.getUrgentForTheDay()
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error loading employee urgent calls:', error);
                    throw error;
                })
            );
    }

    loadEmployeeUrgentCalls = (userId, weekNumber, year) => {
        this.setLoading(true);
        return agent.UrgentCalls.getUrgentCallForEmployee(userId, weekNumber, year)
            .then(
                action((response) => {
                    this.setLoading(false);
                    return response;
                })
            )
            .catch(
                action((error) => {
                    this.setLoading(false);
                    console.error('Error loading employee urgent calls:', error);
                    throw error;
                })
            ).finally(
                action(() => {
                    this.setLoading(false);
                })
            );
    }

    loadEmployeeUrgentCallForDate = (userId, forDate) => {
        this.setLoading(true);
        return agent.UrgentCalls.getUrgentCallForEmployeeForDate(userId, forDate)
            .then(
                action((response) => {
                    this.setLoading(false);
                    return response;
                })
            )
            .catch(
                action((error) => {
                    this.setLoading(false);
                    console.error('Error loading employee urgent calls:', error);
                    throw error;
                })
            ).finally(
                action(() => {
                    this.setLoading(false);
                })
            );
    }

    saveUrgentCalls = (data) => {
        return agent.UrgentCalls.bulkCreate(data)
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error saving urgent calls:', error);
                    throw error;
                })
            )
    }

    updateUrgentCalls = (userId, data) => {
        return agent.UrgentCalls.update(userId, data)
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error saving urgent calls:', error);
                    throw error;
                })
            )
    }

    updateCustomerCareHours = (data) => {
        return agent.UrgentCalls.updateCustomerCareHours(data)
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error saving urgent calls:', error);
                    throw error;
                })
            )
    }

    deleteUrgentCall = (urgentCallId) => {
        this.setLoading(true);
        return agent.UrgentCalls.delete(urgentCallId)
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error deleting urgent call:', error);
                    throw error;
                })
            )
            .finally(
                action(() => {
                    this.setLoading(false);
                })
            );
    }

    async syncToPayroll(userId, weekNumber, year) {
        this.setLoading(true);
        try {
            const response = await agent.UrgentCalls.syncToPayroll({ id: userId, weekNumber, year });
            return response;
        } catch (error) {
            console.error('Error syncing to payroll:', error);
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async desyncToPayroll(userId, weekNumber, year) {
        this.setLoading(true);
        try {
            const response = await agent.UrgentCalls.desyncToPayroll({ id: userId, weekNumber, year });
            return response;
        } catch (error) {
            console.error('Error desyncing to payroll:', error);
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    deleteUrgentCallWeek = (params) => {
        this.setLoading(true);
        return agent.UrgentCalls.deleteWeek(params)
            .then(
                action((response) => {
                    return response;
                })
            )
            .catch(
                action((error) => {
                    console.error('Error deleting urgent call:', error);
                    throw error;
                })
            )
            .finally(
                action(() => {
                    this.setLoading(false);
                })
            );
    }
}

const urgentCallsStore = new UrgentCallsStore();
export default urgentCallsStore;
